<script setup>
import {onMounted, ref} from 'vue';
import {useDataTable} from '@/utils/datatableHelper';
import {useToast} from "vue-toastification";
import {formatDate, showSuccessMessage} from '@/helper';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import ModalDialog from '@/components/modals/ModalDialog.vue';
import AmlQuestionnaireService from '@/services/AmlQuestionnaireService';
import AmlQuestionnaireModal from '@/components/modals/AmlQuestionnaireModal.vue';

import {useI18n} from 'vue-i18n'
import {authData} from '@/services/AuthService';

const {t} = useI18n()

const props = defineProps({
  clientId: Number
});

const toast = useToast();

const isLoading = ref(false);
const datatable = useDataTable(fetchRecords, 'created_at', 10, false);
const rows = ref([]);
const columns = ref([
  {label: t('amlQuestionnairesIndex.date'), field: 'created_at', sortable: false, filterOptions: {enabled: false}, formatFn: (value) => formatDate(value, 'd.m.Y H:i')},
  {label: t('amlQuestionnairesIndex.status'), field: 'state', sortable: false},
  {label: t('amlQuestionnairesIndex.action'), field: 'action', sortable: false},
]);

function fetchRecords(body) {
  body = {...body,...{filter: {client_id: props.clientId}}};
  AmlQuestionnaireService.fetchRecords(props.clientId, body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    rows.value = response.data;
  });
}

function viewAmlQuestionnaire(questionnaire) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(AmlQuestionnaireModal, {amlQuestionnaire: questionnaire, disabled: questionnaire.state !== 'CONCEPT', clientId: props.clientId});
  reveal();
  onConfirm((data) => {
    data.client_id = props.clientId;
    data.creator_id = authData.loggedUser.id;
    AmlQuestionnaireService.saveQuestionnaire(props.clientId, data).then(response => {
      showSuccessMessage(response.message);
      datatable.fetchWithValues();
    });
  });
  onCancel(() => {
  });
}

function deleteAmlQuestionnaire(questionnaire) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('amlQuestionnairesIndex.delete_aml_questionnaire'),
    message: t('amlQuestionnairesIndex.confirm_delete_entry'),
  });
  onConfirm(() => {
    AmlQuestionnaireService.deleteRecord(props.clientId, questionnaire.id).then(() => {
      showSuccessMessage(t('amlQuestionnairesIndex.delete_aml_questionnaire_done'));
      datatable.fetchWithValues();
    });
  });
  reveal();
}

onMounted(() => {
  emitter.on('amlQuestionnaireSaved', () => {
    datatable.fetchWithValues();
  });
})
</script>

<template>
  <div class="page-section">
    <div class="page-section__content">
      <div class="mt-3">
        <vue-good-table
            :columns="columns"
            :rows="rows"
            mode="remote"
            @page-change="datatable.onPageChange"
            @sort-change="datatable.onSortChange"
            @column-filter="datatable.onColumnFilter"
            @per-page-change="datatable.onPerPageChange"
            :totalRows="datatable.totalRecords.value"
            :pagination-options="{
              enabled: true,
              rowsPerPageLabel: t('amlQuestionnairesIndex.entriesPerPage'),
              nextLabel: t('amlQuestionnairesIndex.next'),
              prevLabel: t('amlQuestionnairesIndex.previous'),
              ofLabel: 'z',
            }">
          <template #table-row="props">
            <span v-if="props.column.field === 'state'">
              <span v-if="props.row.state === 'DENIED'"> {{ t('amlQuestionnairesIndex.state_rejected') }} </span>
              <span v-if="props.row.state === 'APPROVED'"> {{ t('amlQuestionnairesIndex.state_confirmed') }} </span>
              <span v-if="props.row.state === 'WAITING'"> {{ t('amlQuestionnairesIndex.state_waiting_confirm') }} </span>
              <span v-if="props.row.state === 'TO_REVIEW'"> {{ t('amlQuestionnairesIndex.state_to_review') }} </span>
            <span v-if="props.row.state === 'CANCELED'"> {{ t('amlQuestionnairesIndex.state_canceled') }} </span>
            <span v-if="props.row.state === 'ACCEPTED'"> {{ t('amlQuestionnairesIndex.state_accepted') }} </span>
            <span v-if="props.row.state === 'CONCEPT'"> {{ t('amlQuestionnairesIndex.state_concept') }} </span>
            </span>
            <span v-if="props.column.field === 'action'">
              <span style="font-weight: bold; float: right">
                <button v-if="props.row.method === 'FORM'" type="button" class="btn btn-link"><i @click="viewAmlQuestionnaire(props.row)" class="mdi mdi-eye"/></button>
                <button type="button" class="btn btn-error" @click="deleteAmlQuestionnaire(props.row)"><i class="mdi mdi-trash-can-outline"/></button>
              </span>
            </span>
          </template>
          <template v-slot:emptystate v-if="!isLoading">
            <div class="text-center">
              {{ t('amlQuestionnairesIndex.no_records_found') }}
            </div>
          </template>
          <template v-slot:emptystate v-if="isLoading">
            <div class="text-center">
              {{ t('amlQuestionnairesIndex.data_loading') }}
            </div>
          </template>
        </vue-good-table>
      </div>
    </div>

  </div>
</template>

<style scoped>

</style>
